import { h } from 'preact';
import { PureComponent, createPortal } from 'preact/compat';
import { css } from 'react-emotion';
import { isMobile } from 'mobile-device-detect';
import Popup from '../Popup/Popup';
import { withContext } from '../../common/context';
import { ZoomViewerPosition, ZoomTrigger, ZoomType, DisplayMode } from '../../typing/enums';
import { getValueFromRange } from '../../utils/number';
import { Events } from '../../utils/events';
import { mouseTracker, clickTrigger, getMouseX, getMouseY } from '../../utils/mouse';
import { ZoomWrapper, ZoomTriggerWrapper } from './Zoom.styled';
import ZoomTip from './components/ZoomTip';
import ZoomImage from './components/ZoomImage';
import ZoomLens from './components/ZoomLens';

class Zoom extends PureComponent {
  state = {
    left: 0,
    top: 0,
    isZoomed: false,
    transition: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.zoomIn !== this.props.zoomIn) {
      if (this.props.zoomIn) {
        this.zoomIn();
        this.onMouseDown();
      } else {
        this.zoomOut();
      }
    }
  }

  componentWillUnmount() {
    this.zoomOut();
    document.body.removeEventListener('keydown', this.handleEsc);
  }

  isInline = () => ZoomType.INLINE === this.props.type && !this.props.viewerContainer;

  isPopup = () => ZoomType.POPUP === this.props.type && !this.props.viewerContainer;

  handleEsc = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.zoomOut();
    }
  };

  getCurrentAsset = () => {
    const getAssetByIndex = index =>
      index && this.props.context.config.selectMediaAssets()[this.props.index];

    const getAssetByPublicId = publicId =>
      this.props.context.config.selectMediaAssets().find(asset => asset.publicId === publicId);

    return (
      this.props.asset ||
      getAssetByIndex(this.props.index) ||
      getAssetByPublicId(this.props.publicId)
    );
  };

  getCursorPos = e => {
    let a;
    let x = this.state.left;
    let y = this.state.top;
    e = e || window.event;

    // calc cursor position only for real user interactions, otherwise keep state values.
    if (e && e.isTrusted) {
      // get cursor's x and y coordinates, relative to the viewer:
      x = getMouseX(e);
      y = getMouseY(e);
      // get the x and y positions of the viewer:
      a = this.wrapper.getBoundingClientRect();
      // consider viewer position and page scrolling:
      x = x - a.left - window.pageXOffset;
      y = y - a.top - window.pageYOffset;
    }

    return {
      x,
      y
    };
  };

  onMove = e => {
    if (e && e.isTrusted && this.mouseTracker) {
      e.preventDefault();
      let { x, y } = this.getCursorPos(e);

      const offsetShadow = this.props.context.config.selectZoomPropsLensShadow() ? 1 : 0;

      const lensWidth = this.props.width / this.props.level;
      const lensHeight = this.props.height / this.props.level;

      // prevent the lens from being positioned outside the image:
      x = getValueFromRange(x, lensWidth / 2, this.props.width - lensWidth / 2 - offsetShadow);
      y = getValueFromRange(y, lensHeight / 2, this.props.height - lensHeight / 2 - offsetShadow);

      // normalize around the center, (0, 0) means center
      x = x - this.props.width / 2;
      y = y - this.props.height / 2;

      // mobile touch behaviour
      if (isMobile && !this.mouseClickTracker) {
        x = x * -1;
        y = y * -1;
      }

      this.setState({
        left: x,
        top: y
      });
    }
  };

  onMouseDown = event => {
    if (!event) {
      event = new MouseEvent('mousedown', {
        view: window,
        bubbles: true,
        cancelable: true
      });
    }
    this.mouseClickTracker = clickTrigger(event);
    this.mouseTracker = mouseTracker(event);
  };

  onMouseUp = event => {
    if (this.mouseClickTracker && this.mouseClickTracker(event)) {
      if (this.state.isZoomed) {
        this.zoomOut();
      } else {
        this.zoomIn(event);
      }
      if (isMobile) {
        event.stopPropagation();
      }
    }
  };

  mouseOver = event => {
    if (this.props.trigger === ZoomTrigger.HOVER) {
      this.zoomIn(event);
    }
  };

  mouseOut = e => {
    if (
      e.offsetX >= this.props.width ||
      e.offsetX < 0 ||
      e.offsetY >= this.props.height ||
      e.offsetY < 0
    ) {
      this.zoomOut();
    }
  };

  touchStart = event => {
    this.onMouseDown(event);
    if (this.state.isZoomed) {
      event.stopPropagation();
    }
  };

  touchEnd = event => {
    this.onMouseUp(event);
    if (this.state.isZoomed) {
      event.stopPropagation();
    }
  };

  getWrapperCoords = () => {
    if (!this.state.isZoomed && !this.state.transition) {
      // Disabled zoom
      if (this.isInline() && !this.isPopup()) {
        return { x: 0, y: 0 };
      } else if (!this.isPopup() && (!this.isInline() || this.props.viewerContainer)) {
        return { x: -1000000, y: -1000000 };
      }
    }

    if (this.wrapper && !this.props.viewerContainer) {
      const { height, width, context } = this.props;
      const position = this.props.viewerPosition;
      const viewerOffset = context.config.selectZoomPropsViewerOffset();
      const rect = this.wrapper.getBoundingClientRect();
      const scrollLeft = window.pageXOffset;
      const scrollTop = window.pageYOffset;
      const displayMode = context.config.selectDisplayPropsMode();
      const displaySpacing = context.config.selectDisplayPropsSpacing();
      const displayColumns = context.config.selectDisplayPropsColumns();
      const isMultiColumns = displayMode === DisplayMode.EXPANDED && displayColumns > 1;
      const offset = isMultiColumns ? displaySpacing : viewerOffset;
      let top;
      if (this.isInline()) {
        top = 0;
      } else if (position === ZoomViewerPosition.BOTTOM) {
        top = scrollTop + rect.top + height + offset;
      } else if (position === ZoomViewerPosition.TOP) {
        top = scrollTop + rect.top - height - offset;
      } else {
        top = rect.top + scrollTop;
      }
      let left;
      if (this.isInline()) {
        left = 0;
      } else if (position === ZoomViewerPosition.RIGHT) {
        left = scrollLeft + rect.right + offset;
      } else if (position === ZoomViewerPosition.LEFT) {
        left = scrollLeft + rect.left - width - offset;
      } else {
        left = rect.left + scrollLeft;
      }

      return { y: top, x: left };
    }

    return { y: 0, x: 0 };
  };

  zoomIn = event => {
    this.setState({ isZoomed: true });
    if (this.props.setZoom) {
      this.props.setZoom(this.props.index);
    }
    if (!this.isPopup()) {
      this.setTransition();
      this.onMove(event);
    }
    this.props.context.events(Events.ZOOM_IN, this.props.publicId);
    this.props.onZoomIn && this.props.onZoomIn();
    document.body.addEventListener('keydown', this.handleEsc);
  };

  zoomOut = () => {
    this.setTransition();
    this.setState({ isZoomed: false });
    if (this.props.onZoomOut) {
      this.props.onZoomOut();
    }
    if (this.props.setZoom) {
      this.props.setZoom(-1);
    }
    this.props.context.events(Events.ZOOM_OUT, this.props.publicId);
    document.body.removeEventListener('keydown', this.handleEsc);
  };

  setTransition = () => {
    const duration = 150;

    this.setState({ transition: true });

    setTimeout(() => {
      if (this.state.transition) {
        this.setState({ transition: false });
      }
    }, duration);
  };

  setContainerStyles = () => {
    if (this.props.viewerContainer) {
      const elem = document.querySelector(this.props.viewerContainer);
      if (elem) {
        if (!this.state.isZoomed && !this.state.transition) {
          elem.classList.remove(this.containerCls);
        } else {
          const cls = css`
            overflow: hidden;
            width: ${this.props.width}px;
            height: ${this.props.height}px;
            ${!elem.style.position ? 'position: relative;' : ''};
          `;
          elem.classList.add((this.containerCls = cls));
        }
      }
    }
  };

  render(props, state) {
    const { config } = props.context;
    const lensWidth = props.width / props.level;
    const lensHeight = props.height / props.level;
    const zoomContainer = props.viewerContainer;
    const wrapperCoords = this.getWrapperCoords();

    if (zoomContainer) {
      this.setContainerStyles();
    }

    const zoomProps = {
      zoomed: state.isZoomed,
      url: props.url,
      width: props.width,
      height: props.height,
      level: props.level,
      top: wrapperCoords.y,
      left: wrapperCoords.x,
      lensLeft: state.left,
      lensTop: state.top,
      radius: config.selectZoomPropsViewerRadius(),
      zIndex: !this.isInline() && config.selectZoomPropsViewerZIndex(),
      bgColor: config.selectThemePropsPrimary(),
      spacing: config.selectZoomPropsViewerOffset(),
      renderToContainer: zoomContainer,
      transition: state.transition // transition on zoom in/out
    };

    return (
      <ZoomWrapper innerRef={elem => (this.wrapper = elem)} data-test="zoom-wrap">
        <ZoomTip />

        {
          // Inline Zoom
          this.isInline() && !this.isPopup() ? <ZoomImage {...zoomProps} /> : null
        }

        {
          // Flyout Zoom
          !this.isInline() && !this.isPopup()
            ? createPortal(
                <ZoomImage {...zoomProps} />,
                (zoomContainer && document.querySelector(zoomContainer)) || document.body
              )
            : null
        }

        {!this.isPopup() && (
          // Zoom Lens
          <ZoomLens
            show={state.isZoomed && !this.isInline() && config.selectZoomPropsShowLens()}
            width={lensWidth}
            height={lensHeight}
            left={state.left}
            top={state.top}
          />
        )}

        {this.isPopup() && this.state.isZoomed ? (
          <Popup
            alt={this.props.alt}
            onClose={this.zoomOut}
            asset={this.getCurrentAsset()}
            {...config.selectZoomPopupProps()}
          />
        ) : null}

        <ZoomTriggerWrapper
          data-test="zoom-trigger"
          onMouseDown={
            !isMobile && props.trigger === ZoomTrigger.CLICK ? this.onMouseDown : undefined
          }
          onMouseUp={!isMobile && props.trigger === ZoomTrigger.CLICK ? this.onMouseUp : undefined}
          onMouseMove={this.onMove}
          onMouseOut={
            !isMobile && this.state.isZoomed && !this.isPopup() ? this.mouseOut : undefined
          }
          onMouseOver={!isMobile && !this.isPopup() ? this.mouseOver : undefined}
          onTouchStart={isMobile ? this.touchStart : undefined}
          onTouchMove={isMobile && state.isZoomed && !this.isPopup() ? this.onMove : undefined}
          onTouchEnd={isMobile ? this.touchEnd : undefined}
          onTouchCancel={isMobile ? this.touchEnd : undefined}
          style={{
            cursor: props.zoomed ? 'zoom-out' : 'zoom-in'
          }}
        />
      </ZoomWrapper>
    );
  }
}

export default withContext(Zoom);
